import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/base/AnimeCard";
import { animeList, categories } from "@/mocks/anime";

export default function AnimeDetail() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === id);
  const category = categories.find((c) => c.type === anime?.category);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (anime) document.title = `${anime.title} - ${anime.tags.join("·")} | 妖精动漫网`;
  }, [id]);

  const related = useMemo(
    () =>
      anime
        ? animeList.filter((a) => a.category === anime.category && a.id !== anime.id).slice(0, 4)
        : [],
    [anime]
  );

  if (!anime || !category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex flex-col items-center justify-center pt-20 bg-background-50">
          <span className="w-16 h-16 rounded-2xl bg-background-100 text-foreground-300 flex items-center justify-center text-3xl">
            <i className="ri-movie-2-line"></i>
          </span>
          <h1 className="mt-4 font-heading text-2xl text-foreground-950">未找到该番剧</h1>
          <Link to="/" className="mt-4 text-primary-600 hover:text-primary-700 text-sm">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const metaItems = [
    { label: "题材分类", value: category.name },
    { label: "首播年份", value: `${anime.year}` },
    { label: "集数", value: anime.episodes },
    { label: "更新状态", value: anime.status },
    { label: "播放热度", value: anime.views },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TVSeries",
            name: anime.title,
            description: anime.description,
            genre: category.name,
            image: anime.image,
            url: `https://updateline.github.io/anime/${anime.id}`,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: anime.rating,
              bestRating: 10,
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://updateline.github.io/" },
                { "@type": "ListItem", position: 2, name: category.name, item: `https://updateline.github.io/category/${category.type}` },
                { "@type": "ListItem", position: 3, name: anime.title },
              ],
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <section className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <nav aria-label="面包屑" className="mb-6">
              <ol className="flex items-center gap-2 flex-wrap text-sm text-foreground-500">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to={`/category/${category.type}`} className="hover:text-primary-600">{category.name}</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-800">{anime.title}</li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="relative rounded-2xl overflow-hidden border border-background-200 shadow-card lg:sticky lg:top-24">
                  <img
                    src={anime.image}
                    alt={`${anime.title} 番剧海报`}
                    className="w-full h-80 sm:h-96 lg:h-[420px] object-cover object-top"
                  />
                  <span
                    className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs text-white ${
                      anime.status === "连载中" ? "bg-accent-500" : "bg-foreground-950/60"
                    }`}
                  >
                    {anime.status}
                  </span>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs">
                    {category.name}
                  </span>
                  {anime.tags.map((t) => (
                    <span key={t} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                      #{t}
                    </span>
                  ))}
                </div>

                <h1 className="mt-4 font-heading text-4xl md:text-5xl text-foreground-950">
                  {anime.title}
                </h1>

                <div className="mt-4 flex items-center gap-6">
                  <span className="inline-flex items-center gap-1.5 text-accent-600">
                    <i className="ri-star-fill text-xl"></i>
                    <span className="font-heading text-3xl text-foreground-950">{anime.rating}</span>
                    <span className="text-sm text-foreground-500">/ 10</span>
                  </span>
                  <span className="h-8 w-px bg-background-200"></span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600">
                    <i className="ri-fire-line text-primary-500"></i>
                    {anime.views} 播放
                  </span>
                  <span className="h-8 w-px bg-background-200"></span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600">
                    <i className="ri-time-line text-secondary-500"></i>
                    {anime.updateInfo}
                  </span>
                </div>

                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {metaItems.map((m) => (
                    <div key={m.label} className="rounded-xl bg-background-100 border border-background-200 p-3">
                      <p className="text-xs text-foreground-500">{m.label}</p>
                      <p className="mt-1 text-sm font-medium text-foreground-900">{m.value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    开始追看
                  </button>
                </div>

                <div className="mt-8">
                  <h2 className="font-heading text-2xl text-foreground-950 mb-3">作品简介</h2>
                  <p className="text-foreground-700 leading-8">{anime.description}</p>
                </div>

                <div className="mt-8 rounded-xl bg-secondary-50 border border-secondary-200 p-4 text-sm text-secondary-800 leading-relaxed">
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    <i className="ri-information-line"></i>
                    播放提示
                  </span>
                  <p className="mt-2">
                    本站为奇幻番剧题材整理与推荐聚合站，具体在线播放请前往各正版平台查看，播放以平台实际上线情况为准。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                同题材推荐
              </h2>
              <Link
                to={`/category/${category.type}`}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            {related.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {related.map((a) => (
                  <AnimeCard key={a.id} anime={a} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-foreground-500">更多同题材作品整理中</p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}